import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import BackButton from '../../components/back-button/back-button';
import FitmentByVehicle from './components/fitment-by-vehicle/fitment-by-vehicle';
import FitmentShopBy from './components/fitment-shop-by/fitment-shop-by';
import FitmentSubTabs from './components/fitment-sub-tabs/fitment-sub-tabs';
import FitmentTabs from './components/fitment-tabs/fitment-tabs';
import {
  CANT_FIND_OPTION_IDS,
  CRUMBS,
  EVENTS,
  PROP_TYPES,
  TABS
} from './fitment-constants';
import { getWheelSize, isOE, toFitmentSize } from './fitment-utils';

import { useModal, usePrevious } from '../../shared/hooks/hooks';
import useBrand from './hooks/use-brand';
import useSize from './hooks/use-size';
import useVehicle from './hooks/use-vehicle';

import { getInitialCrumb } from '../../shared/utils/fitment/fitment';

import './fitment.scss';

const FitmentBySize = lazy(() =>
  import('./components/fitment-by-size/fitment-by-size')
);
const FitmentByBrand = lazy(() =>
  import('./components/fitment-by-brand/fitment-by-brand')
);

function Fitment(props) {
  const {
    canToggleVehicle,
    defaultVehicleLicensePlateAddress,
    getAssemblies,
    getFitment,
    getMakes,
    getModels,
    getOptionalSizes,
    getRideHeights,
    getTireBrands,
    getTireCrossDimensions,
    getTireDiameters,
    getTireDimensions,
    getTrims,
    getWheelBoltPatterns,
    getWheelBrands,
    getWheelDiameters,
    getWheelWidths,
    getYears,
    hideBackButton,
    hideDelete,
    hideEditLink,
    hideLicensePlate,
    hidePartialFitmentOptions,
    hideTabs,
    hideTitle,
    hideVehicleCrumbs,
    isCustomDriverTypeShowing,
    isEditing: isEditingProp,
    isFitmentConfirmed,
    isLicenseFitmentCalled,
    isLicensePlateModalEmbedded,
    isLicensePlateSizeSearch,
    isLoggedIn,
    isOptionsMenuOpenByDefault,
    isShowingRecentFitments: isShowingRecentFitmentsProp,
    isTreadwellStart,
    isViewingOptionalSizes,
    isYearAfterModel,
    onAddVehicleClicked,
    onBackToMyVehicles,
    onEvent,
    onLicensePlateCancel,
    onLicensePlateVehicleConfirmed,
    onSelectFitment,
    onShopProducts: onShopProductsProp,
    onSignInClick,
    onSubmitLicensePlate,
    pendingFitment,
    recentFitments,
    recentTireBrands,
    recentTireSizes,
    recentWheelBrands,
    recentWheelSizes,
    renderAddNewVehicleButton,
    renderDriverTypesInput,
    renderFitmentBanner,
    renderSavedFitmentsMessage,
    renderSelectedFitment,
    renderShopProductsButton,
    renderZipCodeInput,
    requireFitmentConfirmation,
    savedFitments,
    selectedBrandTab: selectedBrandTabProp,
    selectedDriverType,
    selectedFitment,
    selectedSizeTab: selectedSizeTabProp,
    selectedTab: selectedTabProp,
    selectedVehicleCrumb: selectedVehicleCrumbProp,
    selectedVehicleDetails,
    setIsLicenseFitmentCalled,
    setIsLicensePlateSizeSearch,
    setIsViewingOptionalSizes,
    setShowShopByBackButton,
    setShowShopByVehicleOverride,
    shouldAutofocusLicensePlate,
    shouldAutofocusYear,
    shouldCallShopProducts,
    shouldResetOnComplete,
    showAddVehicleOptions,
    showEmptyRecentOrSavedFitments,
    showShopByBackButton,
    showShopByVehicleOverride,
    showShopProductsButton,
    temporaryFitment,
    textOverrides,
    zipCodeValue
  } = props;

  const initialCrumb = getInitialCrumb();
  const prevIsShowingRecentFitmentsProp = usePrevious(
    isShowingRecentFitmentsProp
  );
  const [isEditing, setIsEditing] = useState(Boolean(isEditingProp));
  const [isShowingRecentFitments, setIsShowingRecentFitments] = useState(
    typeof isShowingRecentFitmentsProp === 'boolean'
      ? isShowingRecentFitmentsProp
      : true
  );
  const [selectedBrandTab, setSelectedBrandTab] = useState(
    selectedBrandTabProp || TABS.TIRES
  );
  const [selectedSizeTab, setSelectedSizeTab] = useState(
    selectedSizeTabProp || TABS.TIRES
  );
  const [selectedTab, setSelectedTab] = useState(
    selectedTabProp || TABS.VEHICLE
  );
  const [selectedVehicleCrumb, setSelectedVehicleCrumb] = useState(
    typeof selectedVehicleCrumbProp === 'string'
      ? selectedVehicleCrumbProp
      : initialCrumb
  );

  const [shouldHideShopBySizeTabs, setShouldHideShopBySizeTabs] =
    useState(false);
  const [hasNavigatedFromTireSizeStep, setHasNavigatedFromTireSizeStep] =
    useState(false);
  const [selectedOeOption, setSelectedOeOption] = useState(null);

  const queryErrorEvent = useCallback(
    error => e =>
      void onEvent({
        name: error,
        payload: e
      }),
    [onEvent]
  );

  const { tireBrands, wheelBrands } = useBrand({
    getTireBrands,
    getWheelBrands,
    queryErrorEvent,
    selectedBrandTab,
    selectedTab
  });

  const {
    frontTireSize,
    frontWheelSize,
    isEditingFrontTireSize,
    isEditingFrontWheelSize,
    isEditingRearTireSize,
    isEditingRearWheelSize,
    isTireCodeDescriptionOpen,
    rearTireSize,
    rearWheelSize,
    resetTireCrossDimensions,
    resetTireDiameters,
    resetWheelBoltPatterns,
    resetWheelWidths,
    selectedTireCrossDimension,
    selectedTireDiameter,
    selectedTireHeight,
    selectedTireSizeCrumb,
    selectedTireWidth,
    selectedWheelBoltPattern,
    selectedWheelDiameter,
    selectedWheelSizeCrumb,
    selectedWheelWidth,
    setFrontTireSize,
    setFrontWheelSize,
    setIsEditingFrontTireSize,
    setIsEditingFrontWheelSize,
    setIsEditingRearTireSize,
    setIsEditingRearWheelSize,
    setIsTireCodeDescriptionOpen,
    setRearTireSize,
    setRearWheelSize,
    setSelectedTireCrossDimension,
    setSelectedTireDiameter,
    setSelectedTireHeight,
    setSelectedTireSizeCrumb,
    setSelectedTireWidth,
    setSelectedWheelBoltPattern,
    setSelectedWheelDiameter,
    setSelectedWheelSizeCrumb,
    setSelectedWheelWidth,
    setShowRemoveRearTireConfirmation,
    setShowRemoveRearWheelConfirmation,
    setSizeLimit,
    showRemoveRearTireConfirmation,
    showRemoveRearWheelConfirmation,
    sizeLimit,
    tireCrossDimensions,
    tireDiameters,
    tireDimensions,
    wheelBoltPatterns,
    wheelDiameters,
    wheelWidths
  } = useSize({
    getTireCrossDimensions,
    getTireDiameters,
    getTireDimensions,
    getWheelBoltPatterns,
    getWheelDiameters,
    getWheelWidths,
    queryErrorEvent,
    selectedSizeTab,
    selectedTab
  });
  const { closeModal } = useModal();

  const {
    assemblies,
    makes,
    models,
    onFitment,
    onFitmentSelected,
    resetAssemblies,
    resetMakes,
    resetModels,
    resetRideHeights,
    resetTrims,
    resetYears,
    rideHeights,
    selectedAssembly,
    selectedMake,
    selectedModel,
    selectedRideHeight,
    selectedTrim,
    selectedYear,
    setSelectedAssembly,
    setSelectedMake,
    setSelectedModel,
    setSelectedRideHeight,
    setSelectedTrim,
    setSelectedYear,
    trims,
    updateFitmentSelectionData,
    years
  } = useVehicle({
    canToggleVehicle,
    getAssemblies,
    getMakes,
    getModels,
    getRideHeights,
    getTrims,
    getYears,
    isEditing,
    isFitmentConfirmed,
    isShowingRecentFitments,
    isTreadwellStart,
    isYearAfterModel,
    onEvent,
    pendingFitment,
    queryErrorEvent,
    requireFitmentConfirmation,
    selectedFitment: temporaryFitment || selectedFitment,
    selectedTab,
    selectedVehicleCrumb,
    selectedVehicleDetails,
    setIsEditing,
    setIsShowingRecentFitments,
    setSelectedVehicleCrumb,
    shouldSkipSettingRideHeight: isLicensePlateSizeSearch
  });

  const onEditFitment = useCallback(
    async vehicleCrumbToEdit => {
      await updateFitmentSelectionData(selectedFitment);
      setIsEditing(true);
      const newCrumb = Object.values(CRUMBS).includes(vehicleCrumbToEdit)
        ? vehicleCrumbToEdit
        : CRUMBS.TIRE_WHEEL_SIZE;
      setSelectedVehicleCrumb(newCrumb);
      setIsShowingRecentFitments(false);
    },
    [selectedFitment, updateFitmentSelectionData]
  );

  const onShopProducts = useCallback(
    (selectedAssembly, selectedOptionalSize, sizeQualifier) => {
      setSelectedAssembly(selectedAssembly);
      // TODO: WHAT DO WE DO IF THIS CALL FAILS?
      return getFitment({
        assemblyId: selectedAssembly?.id,
        make: selectedMake,
        rideHeight: selectedRideHeight,
        trimId: selectedTrim?.id,
        uid: isEditing ? selectedFitment?.uid : null,
        vehicleId: isYearAfterModel ? selectedYear?.id : selectedModel?.id,
        year: selectedYear
      }).then(fitment => {
        const updatedFitment = {
          ...fitment,
          rideHeight: selectedRideHeight?.description || null,
          rideHeightId: selectedRideHeight?.id || null,
          rideHeightType: selectedRideHeight?.type || null
        };

        if (selectedOptionalSize) {
          if (selectedOptionalSize.frontTireDescription) {
            updatedFitment.frontTireSize = toFitmentSize(
              selectedOptionalSize?.frontTireDescription
            );
            updatedFitment.frontWheelDiameter = getWheelSize(
              selectedOptionalSize?.frontTireDescription
            );
            updatedFitment.rearTireSize = toFitmentSize(
              selectedOptionalSize?.rearTireDescription
            );
            updatedFitment.rearWheelDiameter = getWheelSize(
              selectedOptionalSize.rearTireDescription
            );
            updatedFitment.isStaggered = true;
          } else {
            updatedFitment.frontTireSize = toFitmentSize(
              selectedOptionalSize.description
            );
            updatedFitment.frontWheelDiameter = getWheelSize(
              selectedOptionalSize.description
            );
            updatedFitment.rearTireSize = null;
            updatedFitment.rearWheelDiameter = null;
            updatedFitment.isStaggered = false;
          }
          updatedFitment.sizeQualifier = sizeQualifier;
          updatedFitment.isOE = isOE(updatedFitment, fitment);
        }

        onShopProductsProp?.(updatedFitment);
        onFitment(updatedFitment);
        return updatedFitment;
      });
    },
    [
      setSelectedAssembly,
      getFitment,
      selectedMake,
      selectedRideHeight,
      selectedTrim,
      selectedYear,
      selectedModel,
      isEditing,
      selectedFitment,
      onShopProductsProp,
      isYearAfterModel,
      onFitment
    ]
  );

  const onRideHeightSelected = useCallback(
    rideHeight => {
      setSelectedRideHeight(rideHeight);
      if (!isLicensePlateSizeSearch) {
        resetAssemblies();
      }

      if (rideHeight) {
        setSelectedVehicleCrumb(CRUMBS.TIRE_WHEEL_SIZE);

        void onEvent({
          name: EVENTS.VEHICLE_RIDE_HEIGHT_SELECTED,
          payload: {
            rideHeight: rideHeight,
            rideHeightOptions: rideHeights
          }
        });
      }
    },
    [
      setSelectedRideHeight,
      isLicensePlateSizeSearch,
      resetAssemblies,
      onEvent,
      rideHeights
    ]
  );

  const onTabSelected = useCallback(
    tab => {
      onEvent({
        name: EVENTS.TAB_SELECTED,
        payload: tab
      });

      if (tab === TABS.VEHICLE || tab === TABS.BRAND) {
        setShowShopByVehicleOverride(false);
        setShouldHideShopBySizeTabs(false);
      }
      setSelectedTab(tab);
    },
    [onEvent, setShowShopByVehicleOverride]
  );

  const resetShopByVehicleState = useCallback(() => {
    if (selectedTab !== TABS.VEHICLE) setSelectedTab(TABS.VEHICLE);
    if (showShopByVehicleOverride) setShowShopByVehicleOverride(false);
  }, [selectedTab, setShowShopByVehicleOverride, showShopByVehicleOverride]);

  const onVehicleCrumbSelected = useCallback(
    crumb => {
      resetShopByVehicleState();
      setSelectedVehicleCrumb(crumb);
    },
    [resetShopByVehicleState]
  );

  const onBackToMyVehiclesClick = useCallback(() => {
    resetShopByVehicleState();
    onBackToMyVehicles?.();
  }, [onBackToMyVehicles, resetShopByVehicleState]);

  const onDisableLicenseSearch = useCallback(() => {
    if (
      selectedVehicleCrumb === CRUMBS.TRIM ||
      selectedVehicleCrumb === CRUMBS.MAKE ||
      selectedVehicleCrumb === CRUMBS.MODEL
    ) {
      setIsLicensePlateSizeSearch(false);
    }
  }, [selectedVehicleCrumb, setIsLicensePlateSizeSearch]);

  useEffect(() => {
    if (
      typeof prevIsShowingRecentFitmentsProp === 'boolean' &&
      prevIsShowingRecentFitmentsProp !== isShowingRecentFitmentsProp
    ) {
      setIsShowingRecentFitments(isShowingRecentFitmentsProp);
    }
  }, [isShowingRecentFitmentsProp, prevIsShowingRecentFitmentsProp]);

  return (
    <div data-testid="fitment" id="fitment-container" styleName="fitment">
      {showShopByBackButton && (
        <BackButton
          onClick={() => {
            setShowShopByBackButton(false);
            setSelectedTab(TABS.SHOP);
          }}
          text="back"
        />
      )}
      <FitmentTabs
        hideTabs={hideTabs}
        onTabSelected={onTabSelected}
        selectedTab={selectedTab}
      />
      <div
        className="fitment-by-vehicle-container"
        styleName={hideTabs ? 'content-no-tabs' : 'content'}
      >
        {selectedTab === TABS.SHOP && (
          <FitmentShopBy
            onClick={tab => {
              onTabSelected(tab);
              setShowShopByBackButton(true);
            }}
          />
        )}
        {(selectedTab === TABS.VEHICLE || showShopByVehicleOverride) && (
          <FitmentByVehicle
            assemblies={assemblies}
            canToggleVehicle={canToggleVehicle}
            defaultVehicleLicensePlateAddress={
              defaultVehicleLicensePlateAddress
            }
            getFitment={getFitment}
            getOptionalSizes={getOptionalSizes}
            hideBackButton={hideBackButton}
            hideDelete={hideDelete}
            hideEditLink={hideEditLink}
            hideLicensePlate={hideLicensePlate}
            hideTitle={hideTitle}
            hideVehicleCrumbs={hideVehicleCrumbs}
            isCustomDriverTypeShowing={isCustomDriverTypeShowing}
            isEditing={isEditing}
            isLicenseFitmentCalled={isLicenseFitmentCalled}
            isLicensePlateModalEmbedded={isLicensePlateModalEmbedded}
            isLicensePlateSizeSearch={isLicensePlateSizeSearch}
            isLoggedIn={isLoggedIn}
            isOptionsMenuOpenByDefault={isOptionsMenuOpenByDefault}
            isShowingRecentFitments={isShowingRecentFitments}
            isTreadwellStart={
              isTreadwellStart ||
              selectedVehicleCrumb === CRUMBS.DRIVING_LOCATION ||
              selectedVehicleCrumb === CRUMBS.DRIVING_TYPE
            }
            isViewingOptionalSizes={isViewingOptionalSizes}
            isYearAfterModel={isYearAfterModel}
            makes={makes}
            models={models}
            onAssemblySelected={assembly => {
              setSelectedAssembly(assembly);
              resetShopByVehicleState();
            }}
            onBackToMyVehicles={onBackToMyVehiclesClick}
            onCrumbSelected={onVehicleCrumbSelected}
            onCustomTireSizeSelected={() => {
              setShowShopByVehicleOverride(true);
              setSelectedTab(TABS.SIZE);
              setSelectedSizeTab(TABS.TIRES);
            }}
            onEditFitment={onEditFitment}
            onEvent={onEvent}
            onFitment={onFitment}
            onFitmentSelected={fitment => {
              onFitmentSelected(fitment).then(() => {
                onEvent({
                  name: EVENTS.VEHICLE_FITMENT_SELECTED,
                  payload: fitment
                });
              });
            }}
            onFitmentsToggled={isOpen => {
              if (isOpen) {
                onEvent({
                  name: EVENTS.VEHICLE_FITMENT_CLEARED
                });
                setIsEditing(false);
                setSelectedYear('');
                resetMakes();
                resetModels();
                resetTrims();
                resetRideHeights();
                resetAssemblies();

                setSelectedVehicleCrumb(initialCrumb);
              } else {
                onAddVehicleClicked?.();
              }
              setIsShowingRecentFitments(isOpen);
            }}
            onLicensePlateCancel={() => {
              onEvent({
                name: EVENTS.LICENSE_PLATE_CANCELLED
              });

              if (onLicensePlateCancel) {
                onLicensePlateCancel();
              } else {
                closeModal();
              }
            }}
            onLicensePlateVehicleConfirmed={vehicleData => {
              onEvent({
                name: EVENTS.LICENSE_PLATE_CONFIRMED,
                payload: vehicleData
              });
              onLicensePlateVehicleConfirmed?.(vehicleData);
            }}
            onMakeSelected={make => {
              setSelectedMake(make);
              onDisableLicenseSearch();
              resetModels();
              resetTrims();
              resetRideHeights();
              resetAssemblies();

              if (isYearAfterModel) {
                resetYears();
              }

              if (make) {
                selectedVehicleCrumb !== CRUMBS.VEHICLE &&
                  setSelectedVehicleCrumb(CRUMBS.MODEL);

                void onEvent({
                  name: EVENTS.VEHICLE_MAKE_SELECTED,
                  payload: make
                });
              }
            }}
            onModelSelected={model => {
              if (model?.id) {
                setSelectedModel(model);
                onDisableLicenseSearch();
                resetTrims();
                resetRideHeights();
                resetAssemblies();

                if (isYearAfterModel) {
                  resetYears();
                  selectedVehicleCrumb !== CRUMBS.VEHICLE &&
                    setSelectedVehicleCrumb(CRUMBS.YEAR);
                } else {
                  setSelectedVehicleCrumb(CRUMBS.TRIM);
                }

                void onEvent({
                  name: EVENTS.VEHICLE_MODEL_SELECTED,
                  payload: model
                });
              } else {
                resetModels();
                resetTrims();
                resetRideHeights();
                resetAssemblies();

                if (model) {
                  // This triggers in a scenario where this value is a model in earlier years but a make in later years. For example, Dodge Ram vs Ram 1500.
                  // This allows users to select the old make, but find the correct make listed under models, which we found users were looking for.
                  setSelectedMake(model?.description);
                  setSelectedVehicleCrumb(
                    process.env.__FF_FITMENT_VEHICLE_CRUMB__ === 'true'
                      ? CRUMBS.VEHICLE
                      : CRUMBS.MODEL
                  );
                }
              }
            }}
            onRideHeightSelected={onRideHeightSelected}
            onSelectFitment={onSelectFitment}
            onShopBySelected={tabOption => {
              if (selectedOeOption?.value === CANT_FIND_OPTION_IDS.SIZE) {
                setSelectedOeOption(null);
              }
              if (tabOption === TABS.SIZE) {
                setShouldHideShopBySizeTabs(true);
              }
              setSelectedTab(tabOption);
            }}
            onShopProducts={onShopProducts}
            onSignInClick={onSignInClick}
            onSubmitLicensePlate={(
              licensePlateQueryVariables,
              onYearSelected,
              onMakeSelected,
              onModelSelected,
              onTrimSelected,
              onAssemblySelected,
              onVehicleConfirmation,
              onCancel,
              getFitment,
              onFitment,
              isLicensePlateModalEmbedded
            ) => {
              onEvent({
                name: EVENTS.LICENSE_PLATE_SUBMITTED,
                payload: licensePlateQueryVariables
              });
              onSubmitLicensePlate?.(
                licensePlateQueryVariables,
                onYearSelected,
                onMakeSelected,
                onModelSelected,
                onTrimSelected,
                onAssemblySelected,
                onVehicleConfirmation,
                onCancel,
                getFitment,
                onFitment,
                isLicensePlateModalEmbedded
              );
            }}
            onTrimSelected={trim => {
              setSelectedTrim(trim);
              onDisableLicenseSearch();
              resetRideHeights();
              resetAssemblies();
              if (trim) {
                void onEvent({
                  name: EVENTS.VEHICLE_TRIM_SELECTED,
                  payload: trim
                });
              }
            }}
            onYearSelected={year => {
              setSelectedYear(year);
              if (!isYearAfterModel) {
                resetMakes();
                resetModels();
              }
              resetTrims();
              resetRideHeights();
              resetAssemblies();
              if (year) {
                if (isYearAfterModel) {
                  setSelectedVehicleCrumb(CRUMBS.TRIM);
                } else {
                  selectedVehicleCrumb !== CRUMBS.VEHICLE &&
                    setSelectedVehicleCrumb(CRUMBS.MAKE);
                }

                void onEvent({
                  name: EVENTS.VEHICLE_YEAR_SELECTED,
                  payload: year
                });
              }
            }}
            recentFitments={recentFitments}
            renderAddNewVehicleButton={renderAddNewVehicleButton}
            renderDriverTypesInput={renderDriverTypesInput}
            renderFitmentBanner={renderFitmentBanner}
            renderSavedFitmentsMessage={renderSavedFitmentsMessage}
            renderSelectedFitment={() => {
              if (shouldResetOnComplete && selectedFitment) {
                resetMakes();
                resetModels();
                resetTrims();
                resetRideHeights();
                resetAssemblies();
                setSelectedYear('');
                setSelectedVehicleCrumb(initialCrumb);
                setIsEditing(false);
                setIsShowingRecentFitments(true);
              } else if (selectedFitment) {
                return renderSelectedFitment({
                  isEditing,
                  onEditFitment,
                  selectedFitment
                });
              } else {
                return null;
              }
            }}
            renderShopProductsButton={renderShopProductsButton}
            renderZipCodeInput={renderZipCodeInput}
            rideHeights={rideHeights}
            savedFitments={savedFitments}
            selectedAssembly={selectedAssembly}
            selectedCrumb={selectedVehicleCrumb}
            selectedDriverType={selectedDriverType}
            selectedFitment={selectedFitment}
            selectedMake={selectedMake}
            selectedModel={selectedModel}
            selectedOeOption={selectedOeOption}
            selectedRideHeight={selectedRideHeight}
            selectedTab={selectedTab}
            selectedTrim={selectedTrim}
            selectedYear={selectedYear}
            setIsLicenseFitmentCalled={setIsLicenseFitmentCalled}
            setIsLicensePlateSizeSearch={setIsLicensePlateSizeSearch}
            setIsShowingRecentFitments={setIsShowingRecentFitments}
            setIsViewingOptionalSizes={setIsViewingOptionalSizes}
            setSelectedOeOption={setSelectedOeOption}
            shouldAutofocusLicensePlate={shouldAutofocusLicensePlate}
            shouldAutofocusYear={shouldAutofocusYear}
            shouldCallShopProducts={shouldCallShopProducts}
            showAddVehicleOptions={showAddVehicleOptions}
            showCustomOptions={
              !hidePartialFitmentOptions && !isEditing && !hideTabs
            }
            showEmptyRecentOrSavedFitments={showEmptyRecentOrSavedFitments}
            showShopProductsButton={showShopProductsButton}
            temporaryFitment={temporaryFitment}
            textOverrides={textOverrides}
            trims={trims}
            years={years}
            zipCodeValue={zipCodeValue}
          />
        )}

        {selectedTab === TABS.SIZE && (
          <Suspense fallback={<div />}>
            <FitmentBySize
              frontTireSize={frontTireSize}
              frontWheelSize={frontWheelSize}
              hasNavigatedFromTireSizeStep={hasNavigatedFromTireSizeStep}
              hidePartialFitmentOptions={hidePartialFitmentOptions}
              hideTabs={showShopByVehicleOverride || shouldHideShopBySizeTabs}
              isEditingFrontTireSize={isEditingFrontTireSize}
              isEditingFrontWheelSize={isEditingFrontWheelSize}
              isEditingRearTireSize={isEditingRearTireSize}
              isEditingRearWheelSize={isEditingRearWheelSize}
              isTireCodeDescriptionOpen={isTireCodeDescriptionOpen}
              onClickNeedHelp={selectedCrumb =>
                void onEvent({
                  name: EVENTS.SIZE_TIRE_NEED_HELP,
                  payload: {
                    selectedCrumb: selectedCrumb
                  }
                })
              }
              onICantFindTireSizeLinkClick={() => {
                showShopByVehicleOverride &&
                  setHasNavigatedFromTireSizeStep(true);
                setShowShopByVehicleOverride(false);
              }}
              onRecentTireSizeRemoved={tireSize =>
                void onEvent({
                  name: EVENTS.SIZE_TIRE_RECENT_REMOVED,
                  payload: tireSize
                })
              }
              onRecentWheelSizeRemoved={wheelSize =>
                void onEvent({
                  name: EVENTS.SIZE_WHEEL_RECENT_REMOVED,
                  payload: wheelSize
                })
              }
              onShopBySelected={setSelectedTab}
              onTabSelected={setSelectedSizeTab}
              onTireCrossDimensionSelected={selectedTireCrossDimension => {
                setSelectedTireCrossDimension(selectedTireCrossDimension);
                resetTireDiameters();
                onEvent({
                  name: EVENTS.SIZE_TIRE_CROSS_DIMENSION_SELECTED,
                  payload: selectedTireCrossDimension
                });
              }}
              onTireCrumbSelected={setSelectedTireSizeCrumb}
              onTireDiameterSelected={selectedTireDiameter => {
                setSelectedTireDiameter(selectedTireDiameter);
                onEvent({
                  name: EVENTS.SIZE_TIRE_DIAMETER_SELECTED,
                  payload: selectedTireDiameter
                });
              }}
              onTireHeightSelected={tireHeight => {
                setSelectedTireHeight(tireHeight);
                resetTireCrossDimensions();
                resetTireDiameters();
                onEvent({
                  name: EVENTS.SIZE_TIRE_HEIGHT_SELECTED,
                  payload: tireHeight
                });
              }}
              onTireScanned={(tireWidth, tireCrossDimension, tireDiameter) => {
                if (isEditingRearTireSize && rearTireSize) {
                  rearTireSize.width = tireWidth;
                  rearTireSize.crossDimension = tireCrossDimension;
                  rearTireSize.diameter = tireDiameter;
                } else if (frontTireSize) {
                  frontTireSize.width = tireWidth;
                  frontTireSize.crossDimension = tireCrossDimension;
                  frontTireSize.diameter = tireDiameter;
                }

                setSelectedTireWidth(tireWidth);
                setSelectedTireCrossDimension(tireCrossDimension);
                setSelectedTireDiameter(tireDiameter);
                setSelectedTireSizeCrumb('');
                setIsEditingFrontTireSize(false);
                setIsEditingRearTireSize(false);
                onEvent({
                  name: EVENTS.SIZE_TIRE_SCANNED,
                  payload: {
                    tireCrossDimension,
                    tireDiameter,
                    tireWidth
                  }
                });
              }}
              onTireSizeSelected={(frontSize, rearSize) => {
                onEvent({
                  name: EVENTS.SIZE_TIRE_SELECTED,
                  payload: {
                    frontSize,
                    rearSize
                  }
                });
              }}
              onTireWidthSelected={tireWidth => {
                setSelectedTireWidth(tireWidth);
                resetTireCrossDimensions();
                resetTireDiameters();
                onEvent({
                  name: EVENTS.SIZE_TIRE_WIDTH_SELECTED,
                  payload: tireWidth
                });
              }}
              onWheelBoltPatternSelected={setSelectedWheelBoltPattern}
              onWheelCrumbSelected={setSelectedWheelSizeCrumb}
              onWheelDiameterSelected={wheelDiameter => {
                resetWheelBoltPatterns();
                resetWheelWidths();
                setSelectedWheelDiameter(wheelDiameter);
              }}
              onWheelSizeSelected={(frontSize, rearSize) => {
                onEvent({
                  name: EVENTS.SIZE_WHEEL_SELECTED,
                  payload: {
                    frontSize,
                    rearSize
                  }
                });
              }}
              onWheelWidthSelected={wheelWidth => {
                resetWheelBoltPatterns();
                setSelectedWheelWidth(wheelWidth);
              }}
              rearTireSize={rearTireSize}
              rearWheelSize={rearWheelSize}
              recentTireSizes={recentTireSizes}
              recentWheelSizes={recentWheelSizes}
              selectedTab={selectedSizeTab}
              selectedTireCrossDimension={selectedTireCrossDimension}
              selectedTireCrumb={selectedTireSizeCrumb}
              selectedTireDiameter={selectedTireDiameter}
              selectedTireHeight={selectedTireHeight}
              selectedTireWidth={selectedTireWidth}
              selectedWheelBoltPattern={selectedWheelBoltPattern}
              selectedWheelCrumb={selectedWheelSizeCrumb}
              selectedWheelDiameter={selectedWheelDiameter}
              selectedWheelWidth={selectedWheelWidth}
              setFrontTireSize={setFrontTireSize}
              setFrontWheelSize={setFrontWheelSize}
              setHasNavigatedFromTireSizeStep={setHasNavigatedFromTireSizeStep}
              setIsEditingFrontTireSize={setIsEditingFrontTireSize}
              setIsEditingFrontWheelSize={setIsEditingFrontWheelSize}
              setIsEditingRearTireSize={setIsEditingRearTireSize}
              setIsEditingRearWheelSize={setIsEditingRearWheelSize}
              setIsTireCodeDescriptionOpen={setIsTireCodeDescriptionOpen}
              setRearTireSize={setRearTireSize}
              setRearWheelSize={setRearWheelSize}
              setSelectedTireCrossDimension={setSelectedTireCrossDimension}
              setSelectedTireDiameter={setSelectedTireDiameter}
              setSelectedTireWidth={setSelectedTireWidth}
              setSelectedWheelBoltPattern={setSelectedWheelBoltPattern}
              setSelectedWheelDiameter={setSelectedWheelDiameter}
              setSelectedWheelWidth={setSelectedWheelWidth}
              setShowRemoveRearTireConfirmation={
                setShowRemoveRearTireConfirmation
              }
              setShowRemoveRearWheelConfirmation={
                setShowRemoveRearWheelConfirmation
              }
              setShowShopByVehicleOverride={setShowShopByVehicleOverride}
              setSizeLimit={setSizeLimit}
              showRemoveRearTireConfirmation={showRemoveRearTireConfirmation}
              showRemoveRearWheelConfirmation={showRemoveRearWheelConfirmation}
              sizeLimit={sizeLimit}
              tireCrossDimensions={tireCrossDimensions}
              tireDiameters={tireDiameters}
              tireDimensions={tireDimensions}
              wheelBoltPatterns={wheelBoltPatterns}
              wheelDiameters={wheelDiameters}
              wheelWidths={wheelWidths}
            />
          </Suspense>
        )}

        {selectedTab === TABS.BRAND && (
          <Suspense fallback={<div />}>
            <FitmentByBrand
              onRecentTireBrandRemoved={tireBrand =>
                void onEvent({
                  name: EVENTS.BRAND_TIRE_RECENT_REMOVED,
                  payload: tireBrand
                })
              }
              onRecentWheelBrandRemoved={wheelBrand =>
                void onEvent({
                  name: EVENTS.BRAND_WHEEL_RECENT_REMOVED,
                  payload: wheelBrand
                })
              }
              onTabSelected={setSelectedBrandTab}
              onTireBrandSelected={tireBrand => {
                onEvent({
                  name: EVENTS.BRAND_TIRE_SELECTED,
                  payload: tireBrand
                });
              }}
              onWheelBrandSelected={wheelBrand => {
                onEvent({
                  name: EVENTS.BRAND_WHEEL_SELECTED,
                  payload: wheelBrand
                });
              }}
              recentTireBrands={recentTireBrands}
              recentWheelBrands={recentWheelBrands}
              selectedTab={selectedBrandTab}
              tireBrands={tireBrands}
              wheelBrands={wheelBrands}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}

Fitment.propTypes = {
  canToggleVehicle: PropTypes.bool,
  defaultVehicleLicensePlateAddress: PropTypes.object,
  getAssemblies: PropTypes.func.isRequired,
  getFitment: PropTypes.func.isRequired,
  getMakes: PropTypes.func.isRequired,
  getModels: PropTypes.func.isRequired,
  getOptionalSizes: PropTypes.func.isRequired,
  getRideHeights: PropTypes.func,
  getTireBrands: PropTypes.func.isRequired,
  getTireCrossDimensions: PropTypes.func.isRequired,
  getTireDiameters: PropTypes.func.isRequired,
  getTireDimensions: PropTypes.func.isRequired,
  getTrims: PropTypes.func.isRequired,
  getWheelBoltPatterns: PropTypes.func.isRequired,
  getWheelBrands: PropTypes.func.isRequired,
  getWheelDiameters: PropTypes.func.isRequired,
  getWheelWidths: PropTypes.func.isRequired,
  getYears: PropTypes.func.isRequired,
  hideBackButton: PropTypes.bool,
  hideDelete: PropTypes.bool,
  hideEditLink: PropTypes.bool,
  hideLicensePlate: PropTypes.bool,
  hidePartialFitmentOptions: PropTypes.bool,
  hideTabs: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  hideTitle: PropTypes.bool,
  hideVehicleCrumbs: PropTypes.bool,
  isCustomDriverTypeShowing: PropTypes.bool,
  isEditing: PropTypes.bool,
  isFitmentConfirmed: PropTypes.bool,
  isLicenseFitmentCalled: PropTypes.bool,
  isLicensePlateModalEmbedded: PropTypes.bool,
  isLicensePlateSizeSearch: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isOptionsMenuOpenByDefault: PropTypes.bool,
  isShowingRecentFitments: PropTypes.bool,
  isTreadwellStart: PropTypes.bool,
  isViewingOptionalSizes: PropTypes.bool,
  isYearAfterModel: PropTypes.bool,
  onAddVehicleClicked: PropTypes.func,
  onBackToMyVehicles: PropTypes.func,
  onEvent: PropTypes.func.isRequired,
  onLicensePlateCancel: PropTypes.func,
  onLicensePlateVehicleConfirmed: PropTypes.func,
  onSelectFitment: PropTypes.func,
  onShopProducts: PropTypes.func,
  onSignInClick: PropTypes.func.isRequired,
  onSubmitLicensePlate: PropTypes.func,
  pendingFitment: PROP_TYPES.fitment,
  recentFitments: PropTypes.arrayOf(PROP_TYPES.fitment),
  recentTireBrands: PropTypes.arrayOf(PROP_TYPES.tireWheelBrand).isRequired,
  recentTireSizes: PropTypes.arrayOf(PROP_TYPES.tireSizeSelection).isRequired,
  recentWheelBrands: PropTypes.arrayOf(PROP_TYPES.tireWheelBrand).isRequired,
  recentWheelSizes: PropTypes.arrayOf(PROP_TYPES.wheelSizeSelection).isRequired,
  renderAddNewVehicleButton: PropTypes.func,
  renderDriverTypesInput: PropTypes.func,
  renderFitmentBanner: PropTypes.func,
  renderSavedFitmentsMessage: PropTypes.func,
  renderSelectedFitment: PropTypes.func,
  renderShopProductsButton: PropTypes.func,
  renderZipCodeInput: PropTypes.func,
  requireFitmentConfirmation: PropTypes.bool,
  savedFitments: PropTypes.arrayOf(PROP_TYPES.fitment),
  selectedBrandTab: PropTypes.string,
  selectedDriverType: PropTypes.string,
  selectedFitment: PROP_TYPES.fitment,
  selectedSizeTab: PropTypes.string,
  selectedTab: PropTypes.string,
  selectedVehicleCrumb: PropTypes.string,
  selectedVehicleDetails: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    trimDesc: PropTypes.string,
    trimId: PropTypes.string,
    vehicleId: PropTypes.string,
    year: PropTypes.string
  }),
  setIsLicenseFitmentCalled: PropTypes.func,
  setIsLicensePlateSizeSearch: PropTypes.func,
  setIsViewingOptionalSizes: PropTypes.func,
  setShowShopByBackButton: PropTypes.bool,
  setShowShopByVehicleOverride: PropTypes.func,
  shouldAutofocusLicensePlate: PropTypes.bool,
  shouldAutofocusYear: PropTypes.bool,
  shouldCallShopProducts: PropTypes.bool,
  shouldResetOnComplete: PropTypes.bool,
  showAddVehicleOptions: PropTypes.bool,
  showEmptyRecentOrSavedFitments: PropTypes.bool,
  showShopByBackButton: PropTypes.bool,
  showShopByVehicleOverride: PropTypes.bool,
  showShopProductsButton: PropTypes.bool,
  temporaryFitment: PROP_TYPES.fitment,
  textOverrides: PropTypes.object,
  zipCodeValue: PropTypes.string
};

export { FitmentByVehicle, FitmentSubTabs };
export default Fitment;
